import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authApi } from '../api/auth';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/shared/ui/atoms/Card';
import { Input } from '@/shared/ui/atoms/Input';
import { Button } from '@/shared/ui/atoms/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/atoms/Form';
import { TrendingUp, KeyRound } from 'lucide-react';

const resetPasswordSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    confirmation_code: z
      .string()
      .length(6, 'The verification code must be exactly 6 digits')
      .regex(/^\d+$/, 'Verification code must contain digits only'),
    new_password: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Must contain at least one special character'),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

/**
 * ResetPasswordPage Component
 *
 * Accepts the 6-digit OTP code received via email and sets a new permanent password
 * directly in AWS Cognito.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';

  const resetMutation = useMutation({
    mutationFn: (values: ResetPasswordFormValues) =>
      authApi.confirmForgotPasswordCognito({
        email: values.email,
        confirmation_code: values.confirmation_code,
        new_password: values.new_password,
      }),
    onSuccess: () => {
      toast.success('Your password has been reset successfully. Please sign in.');
      navigate('/login');
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to reset password. Check your code and try again.';
      toast.error(message);
    },
  });

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: emailFromUrl,
      confirmation_code: '',
      new_password: '',
      confirm_password: '',
    },
  });

  function onSubmit(data: ResetPasswordFormValues) {
    resetMutation.mutate(data);
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4 pt-safe pb-safe">
      <div className="w-full max-w-sm flex flex-col gap-6">
        {/* Logo Header */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary">
            <TrendingUp className="size-5 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Cartera</h1>
          <p className="text-sm text-muted-foreground">Personal finance, simplified.</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 text-primary">
              <KeyRound className="size-5" />
              <CardTitle>Set new password</CardTitle>
            </div>
            <CardDescription>
              Enter the 6-digit code sent to your email and your new password below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmation_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>6-Digit Verification Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="123456"
                          maxLength={6}
                          className="tracking-widest font-mono text-center text-lg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="new_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="At least 12 characters"
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Re-enter your new password"
                          autoComplete="new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full mt-2" disabled={resetMutation.isPending}>
                  {resetMutation.isPending ? 'Updating password…' : 'Reset password'}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link to="/login" className="text-primary underline-offset-4 hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
